import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from 'src/app/services/common/alert.service';
import { PageName, UserTypeEnum } from 'src/app/services/common/constant.service';
import { StorageKey, StorageService } from 'src/app/services/common/storage.service';
import { UserService } from 'src/app/services/user.service';
import { ConfirmDailogComponent } from 'src/app/shared/widgets/confirm-dailog/confirm-dailog.component';
import { catchError, count, first, forkJoin, Observable, of, take } from 'rxjs';
import { GlobalVariables } from 'global-variables';
import { Clipboard } from '@angular/cdk/clipboard';
import { getUsersRouteBase } from 'src/app/components/users/users-route.util';
import { AdminResetPasswordDialogComponent } from 'src/app/components/users/admin-reset-password-dialog/admin-reset-password-dialog.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent implements OnInit {

  userID: any = "";
  currentUserData: any = [];
  profile_photo: any = "../../../../assets/images/default-image.png";
  isOrganizationUser: any = false;

  userData: any = [];
  isClient = false;

  loading = true;
  isEditRights = false;
  slug: string = '';
  url: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private storageService: StorageService,
    private clipboard: Clipboard,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
   
    this.currentUserData = JSON.parse(this.storageService.getValue(StorageKey.currentUser));
    if (this.currentUserData) {
      this.userID = this.currentUserData.id;
      this.slug = this.currentUserData?.slug || '';
      this.setPageRights();
      this.getUserDetail();
    }
  }

  setPageRights(): void {
    const menuRights = this.storageService.getPageMenuRights(PageName.USERS);
    this.isEditRights = menuRights?.isEdit ?? false;
  }

  async getUserDetail() {
    this.loading = true;
    this.userService.detail(this.userID).subscribe({
      next: async (response: any) => {
        this.loading = false;
        if (response) {
          this.userData = response.data;

          this.slug = response.data?.slug || this.currentUserData?.slug || '';
          const base = GlobalVariables.couponShareURL;
          const identifier = this.slug || response.data?.id || this.userID;
          this.url = `${base}view/${identifier}`;

          this.isOrganizationUser = response.data.userType.includes(UserTypeEnum.ORGANIZATION) || response.data.userType.includes(UserTypeEnum.BASE_ORGANIZATION) || response.data.userType.includes(UserTypeEnum.ORGANIZATION_CATALOG) || response.data.userType.includes(UserTypeEnum.ORGANIZATION_LITE);
          if (response.data.userType.some((el: string) => [UserTypeEnum.VP, UserTypeEnum.DIRECTOR, UserTypeEnum.PARTNER].includes(el))) this.isClient = true;
          if (this.isOrganizationUser) {
            if (this.userData?.company_logo) {
              this.profile_photo = this.userData?.company_logo;
            }
            else {
              this.profile_photo = "";
            }
          }
          else {
            if (this.userData?.profile_photo) {
              this.profile_photo = this.userData?.profile_photo;
            }
          }
        }
      },
      error: () => {
        this.loading = false;
        this.alertService.showError('Failed to load user profile');
      },
    });
  }

  valueChangeEditProfile() {
    this.getUserDetail();
  }

  onClickUpgrade() {
    this.userService.upgrade({ userId: this.userID }).pipe(first()).subscribe({
      next: (response) => {
        if (response.success) {
        }
      }
    });
  }

  onClickURLCopy() {
    if (this.currentUserData) {
      this.clipboard.copy(GlobalVariables.angularURL + '#/partner-register?businessId=' + this.currentUserData.id);
      this.alertService.showSuccess("URL copied")
    }
  }

  openSite(): void {
    const activeSlug = this.slug || this.userData?.slug || this.currentUserData?.slug;
    if (!activeSlug) {
      this.alertService.showError('Slug is required to view the site. Please update the slug in profile and try again.');
      return;
    }

    const base = GlobalVariables.couponShareURL;
    const targetUrl = this.url || `${base}view/${activeSlug}`;

    const w = window.open(targetUrl, '_blank');
    if (w) {
      w.opener = null;
    } else {
      window.location.href = targetUrl;
    }
  }

  viewProfile(): void {
    this.openSite();
  }

  get displayName(): string {
    if (!this.userData) return '';
    return [this.userData.firstName, this.userData.lastName].filter(Boolean).join(' ').trim();
  }

  get initials(): string {
    const parts = this.displayName.split(' ').filter(Boolean);
    if (!parts.length) return '?';
    return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
  }

  backToList(): void {
    this.router.navigate([getUsersRouteBase(this.router)]);
  }

  editUser(): void {
    this.router.navigate([`${getUsersRouteBase(this.router)}/detail`], {
      queryParams: { ID: this.userID, Type: this.userData?.userType },
    });
  }

  toggleActive(): void {
    const next = !this.userData.isActive;
    this.userService.active(this.userID, next).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.userData.isActive = next;
        }
      },
      error: () => this.alertService.showError('Failed to update account status'),
    });
  }

  resetPassword(): void {
    this.dialog.open(AdminResetPasswordDialogComponent, {
      width: '420px',
      disableClose: true,
      data: { userId: this.userID, userName: this.displayName || this.userData?.email },
    });
  }

  resendVerification(): void {
    const dialogRef = this.dialog.open(ConfirmDailogComponent, {
      disableClose: true,
      data: {
        title: 'Resend verification email?',
        subTitle: this.userData?.email,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.sendnotification({ userId: this.userID }).subscribe();
      }
    });
  }

  toggleFooterBanner(): void {
    const next = this.userData.isBannerActive === false;
    this.userService.usersActiveBanner(this.userID, { isBannerActive: next }).subscribe({
      next: (res: any) => {
        if (res?.success) {
          const saved = res.data?.isBannerActive;
          this.userData.isBannerActive = saved === true || saved === false ? saved : next;
        }
      },
      error: () => this.alertService.showError('Failed to update footer banner status'),
    });
  }
}